import { useState, useEffect } from 'react';
import { Modal, TextInput, Button, Stack, Group, Text } from '@mantine/core';
import { useProfile, type ProfileData } from '../hooks/useProfile';

interface ProfileModalProps {
  opened: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export function ProfileModal({ opened, onClose, onSave }: ProfileModalProps) {
  const { profileData, isSaving, error, updateProfile } = useProfile();
  const [formData, setFormData] = useState<ProfileData>(profileData);

  // Sync form data when profile data changes or modal opens
  useEffect(() => {
    if (opened) {
      setFormData(profileData);
    }
  }, [opened, profileData]);

  const handleChange = (field: keyof ProfileData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    const success = await updateProfile(formData);
    if (success) {
      onSave?.();
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Profile"
      size="lg"
      styles={{
        header: {
          backgroundColor: 'var(--color-bg-secondary)',
          borderBottom: '1px solid var(--color-border)',
        },
        title: {
          color: 'var(--color-accent-primary)',
          fontWeight: 600,
          fontSize: '1.25rem',
        },
        body: {
          backgroundColor: 'var(--color-bg-primary)',
          padding: '1.5rem',
        },
        content: {
          backgroundColor: 'var(--color-bg-primary)',
          border: '1px solid rgba(0, 240, 255, 0.15)',
        },
        close: {
          color: 'var(--color-text-secondary)',
          '&:hover': {
            backgroundColor: 'var(--color-bg-tertiary)',
          },
        },
      }}
    >
      <Stack gap="md">
        <Group grow>
          <TextInput
            label="First Name"
            placeholder="John"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            classNames={{ input: 'input' }}
          />
          <TextInput
            label="Last Name"
            placeholder="Doe"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            classNames={{ input: 'input' }}
          />
        </Group>

        <TextInput
          label="Date of Birth"
          type="date"
          value={formData.birthDate}
          onChange={handleChange('birthDate')}
          classNames={{ input: 'input' }}
        />

        <Group grow>
          <TextInput
            label="Email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={handleChange('email')}
            classNames={{ input: 'input' }}
          />
          <TextInput
            label="Phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={formData.phone}
            onChange={handleChange('phone')}
            classNames={{ input: 'input' }}
          />
        </Group>

        <TextInput
          label="Street Address"
          placeholder="123 Main St"
          value={formData.addressLine}
          onChange={handleChange('addressLine')}
          classNames={{ input: 'input' }}
        />

        <Group grow>
          <TextInput
            label="City"
            placeholder="Boston"
            value={formData.city}
            onChange={handleChange('city')}
            classNames={{ input: 'input' }}
          />
          <TextInput
            label="State"
            placeholder="MA"
            value={formData.state}
            onChange={handleChange('state')}
            classNames={{ input: 'input' }}
          />
          <TextInput
            label="ZIP Code"
            placeholder="02101"
            value={formData.postalCode}
            onChange={handleChange('postalCode')}
            classNames={{ input: 'input' }}
          />
        </Group>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={onClose}
            disabled={isSaving}
            styles={{
              root: {
                color: 'var(--color-text-secondary)',
                '&:hover': {
                  backgroundColor: 'var(--color-bg-tertiary)',
                },
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            className="btn"
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
